/**
 * apps/battle/client/src/terminal/hooks/useKeyboard.ts
 * Single window-level keyboard listener. Each subscriber registers an array of
 * key handlers; the hook tears them down on unmount.
 *
 * - Ignores keystrokes when an <input> / <textarea> / contenteditable holds focus,
 *   unless `allowInInputs` is set on the handler (e.g. Ctrl+Enter for long-text submit).
 * - Matches keys case-insensitively for letter keys.
 */
import { useEffect } from 'react';

export interface KeyHandler {
  /**
   * Key to match. Compared case-insensitively against e.key.
   * Special tokens: 'Enter', 'CtrlEnter' (Ctrl|Cmd + Enter), 'Escape'.
   */
  key: string;
  handler: (e: KeyboardEvent) => void;
  /** Default false — handler is suppressed while typing into an input/textarea. */
  allowInInputs?: boolean;
  /** Default true — preventDefault on match (avoids the browser's default like Enter newline in inputs). */
  preventDefault?: boolean;
}

function isEditableTarget(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  const tag = t.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (t.isContentEditable) return true;
  return false;
}

export function useKeyboard(handlers: KeyHandler[], enabled = true) {
  useEffect(() => {
    if (!enabled || handlers.length === 0) return;
    function onKey(e: KeyboardEvent) {
      const inField = isEditableTarget(e.target);
      for (const h of handlers) {
        const keyMatches = matchKey(h.key, e);
        if (!keyMatches) continue;
        if (inField && !h.allowInInputs) continue;
        if (h.preventDefault !== false) e.preventDefault();
        h.handler(e);
        return;
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handlers, enabled]);
}

function matchKey(spec: string, e: KeyboardEvent): boolean {
  if (spec === 'CtrlEnter') {
    return e.key === 'Enter' && (e.ctrlKey || e.metaKey);
  }
  if (spec === 'Enter') {
    return e.key === 'Enter' && !e.ctrlKey && !e.metaKey && !e.shiftKey;
  }
  if (spec === 'Escape') {
    return e.key === 'Escape';
  }
  // Single character match, case-insensitive
  return e.key.toLowerCase() === spec.toLowerCase();
}
